import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, 
    Typography, 
    TextField, 
    IconButton, 
    Paper, 
    Avatar,
    CircularProgress,
    Alert,
    InputAdornment,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { 
    Send as SendIcon, 
    SmartToy as RobotIcon,
    Mic as MicIcon,
    CalendarMonth,
    Groups,
    Summarize,
    Lightbulb
} from '@mui/icons-material';
import api from '../api/axios';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { parseUtc } from '../utils/time';


const AIChatPanel = ({ embedded = false }) => {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);

    const startListening = () => {
        if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
            alert('Speech recognition isn\'t supported in this browser — Chrome/Edge on https only.');
            return;
        }
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = 'en-AU';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.onresult = (event) => {
            setInput(event.results[0][0].transcript);
            setListening(false);
        };
        recognition.onend = () => setListening(false);
        recognition.onerror = (e) => {
            setListening(false);
            if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
                alert('Microphone permission denied — check the browser\'s mic permission for this site.');
            }
        };
        recognition.start();
        setListening(true);
        recognitionRef.current = recognition;
    };

    const stopListening = () => {
        recognitionRef.current?.stop();
        setListening(false);
    };

    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        const fetchHistory = async () => {
            if (!user) return;
            try {
                // Fetch private AI chat history
                const response = await api.get(`/messages/conversation/manager_ai_private/${user.user_id}`);
                setMessages(response.data);
            } catch (err) {
                console.error('Failed to fetch AI history:', err);
                setError('Failed to load chat history.');
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [user]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async (e) => {
        if (e) e.preventDefault();
        if (!input.trim() || sending) return;

        const userText = input;
        setInput('');
        setSending(true);
        
        // Optimistic update
        const tempMsg = {
            id: Date.now(),
            sender_type: 'manager',
            content: userText,
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempMsg]);

        try {
            const response = await api.post('/ai/chat/private', {
                message: userText
            });

            if (response.data.success) {
                const aiMsg = {
                    id: Date.now() + 1,
                    sender_type: 'ai',
                    content: response.data.response,
                    created_at: new Date(response.data.timestamp).toISOString()
                };
                setMessages(prev => [...prev, aiMsg]);
            }
        } catch (err) {
            console.error('Failed to send private message:', err);
            setError('Failed to get response from AI.');
        } finally {
            setSending(false);
        }
    };

    if (loading && messages.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#F8FAFC' }}>
            {/* Messages Area */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                {messages.length === 0 && !loading && (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <RobotIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.6 }}>
                            I'm your AI assistant. Ask me anything about jobs, schedule, or team.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2 }}>
                            {[
                                { icon: <CalendarMonth sx={{ fontSize: 16 }} />, text: "What's on tomorrow?" },
                                { icon: <Groups sx={{ fontSize: 16 }} />, text: "Who's available this afternoon?" },
                                { icon: <Summarize sx={{ fontSize: 16 }} />, text: "Jobs needing attention" },
                                { icon: <Lightbulb sx={{ fontSize: 16 }} />, text: "Suggest schedule optimizations" },
                            ].map((suggestion, i) => (
                                <Paper
                                    key={i}
                                    elevation={0}
                                    onClick={() => { setInput(suggestion.text); }}
                                    sx={{
                                        p: 1.5, borderRadius: 2,
                                        border: '1px solid #E2E8F0',
                                        cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        transition: 'all 0.15s',
                                        '&:hover': { bgcolor: '#F0F9FF', borderColor: '#3B82F6' }
                                    }}
                                >
                                    <Box sx={{ color: '#3B82F6' }}>{suggestion.icon}</Box>
                                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                                        {suggestion.text}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                )}

                {messages.map((msg) => {
                    const isOwn = msg.sender_type === 'manager' || msg.sender_type === 'admin';
                    return (
                        <Box 
                            key={msg.id} 
                            sx={{ 
                                alignSelf: isOwn ? 'flex-end' : 'flex-start',
                                maxWidth: '85%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isOwn ? 'flex-end' : 'flex-start'
                            }}
                        >
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2,
                                    bgcolor: isOwn ? '#3B82F6' : 'white',
                                    color: isOwn ? 'white' : 'text.primary',
                                    border: isOwn ? 'none' : '1px solid #E2E8F0',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                <Typography variant="body2">{msg.content}</Typography>
                            </Paper>
                            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', px: 0.5 }}>
                                {format(parseUtc(msg.created_at), 'h:mm a')}
                            </Typography>
                        </Box>
                    );
                })}
                <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Paper 
                component="form" 
                onSubmit={handleSend}
                elevation={3} 
                sx={{ 
                    p: 1.5, 
                    bgcolor: 'white', 
                    borderRadius: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                <TextField
                    fullWidth
                    placeholder="Ask anything..."
                    size="small"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={sending}
                    autoComplete="off"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#F8FAFC'
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={listening ? stopListening : startListening}
                                    color={listening ? 'error' : 'default'}
                                    title={listening ? 'Stop listening' : 'Speak'}
                                >
                                    <MicIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <IconButton 
                    color="primary" 
                    type="submit" 
                    disabled={!input.trim() || sending}
                    sx={{ 
                        bgcolor: input.trim() ? '#3B82F6' : 'transparent',
                        color: input.trim() ? 'white' : 'inherit',
                        '&:hover': { bgcolor: '#2563EB' }
                    }}
                >
                    {sending ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
            </Paper>
        </Box>
    );
};

export default AIChatPanel;
